import { cn } from '@/lib/utils'
import { DocumentRichEditor } from '../editor/DocumentRichEditor'
import type { DocBlock, DocField, SablonSurum } from '../types'
import { DocFieldControl } from './DocFieldControl'
import { SafeHtmlFrame } from './SafeHtmlFrame'
import { TableBlock } from './TableBlock'

type MetaPatch = Partial<Pick<SablonSurum, 'editorFont' | 'editorFontSize' | 'lineHeight'>>

type Props = {
  block: DocBlock
  fieldMap: Map<string, DocField>
  selectedFieldId?: string | null
  sampleValues?: Record<string, string>
  fillMode: 'empty' | 'sample'
  editable: boolean
  surum: SablonSurum
  onSelectField?: (id: string | null) => void
  onUpdateBlock: (block: DocBlock) => void
  onUpdateSurumMeta?: (patch: MetaPatch) => void
}

export function CanvasBlock({
  block,
  fieldMap,
  selectedFieldId,
  sampleValues,
  fillMode,
  editable,
  surum,
  onSelectField,
  onUpdateBlock,
  onUpdateSurumMeta,
}: Props) {
  if (block.kind === 'heading') {
    const Tag = block.level === 1 ? 'h1' : 'h2'
    return (
      <Tag
        className={cn(
          'font-bold tracking-tight text-foreground',
          block.level === 1 ? 'text-center text-sm uppercase tracking-wide' : 'text-[13px] text-brand-navy dark:text-foreground',
        )}
      >
        {block.text}
      </Tag>
    )
  }

  if (block.kind === 'divider') {
    return <hr className="my-1 border-border" />
  }

  if (block.kind === 'table') {
    return (
      <TableBlock
        rows={block.rows}
        tone={block.headerTone}
        editable={editable}
        onChange={(rows) => onUpdateBlock({ ...block, rows })}
        onToneChange={(headerTone) => onUpdateBlock({ ...block, headerTone })}
      />
    )
  }

  if (block.kind === 'richtext') {
    if (!editable) {
      return <SafeHtmlFrame html={block.html} fontFamily={block.fontFamily ?? surum.editorFont} fontSize={block.fontSize ?? surum.editorFontSize} />
    }
    return (
      <div onClick={(e) => e.stopPropagation()} style={{ fontFamily: block.fontFamily ?? surum.editorFont }}>
        <DocumentRichEditor
          value={block.html}
          onChange={(html) => onUpdateBlock({ ...block, html })}
          fontFamily={block.fontFamily ?? surum.editorFont}
          fontSize={block.fontSize ?? surum.editorFontSize}
          lineHeight={surum.lineHeight}
          onFontFamilyChange={(fontFamily) => {
            onUpdateBlock({ ...block, fontFamily })
            onUpdateSurumMeta?.({ editorFont: fontFamily })
          }}
          onFontSizeChange={(fontSize) => {
            onUpdateBlock({ ...block, fontSize })
            onUpdateSurumMeta?.({ editorFontSize: fontSize })
          }}
          minHeight="3.5rem"
          hideToolbar
          className="rounded-md border-border/60"
        />
      </div>
    )
  }

  const field = fieldMap.get(block.fieldId)
  if (!field) return null
  const sample = fillMode === 'sample' ? sampleValues?.[field.variable ?? field.key] ?? sampleValues?.[field.key] : undefined
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DocFieldControl
        field={field}
        selected={selectedFieldId === field.id}
        sample={sample}
        mode={editable ? 'editor' : 'preview'}
        fillMode={fillMode}
        showSourceBadge={editable}
        onSelect={() => onSelectField?.(field.id)}
      />
    </div>
  )
}
